import * as DialogPrimitive from '@radix-ui/react-dialog'
import { type LucideIcon, X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'
import { IconCircle } from '../atoms/IconCircle'

/**
 * Modal — Radix Dialog with DS styling.
 * Pencil: Ip7d5 (Standard) · IDVg8 (Destructive) · Y1ecY (Centered).
 * Provides focus trap, scroll lock, escape close, backdrop click close out of the box.
 */
export const Modal = DialogPrimitive.Root
export const ModalTrigger = DialogPrimitive.Trigger
export const ModalClose = DialogPrimitive.Close
export const ModalPortal = DialogPrimitive.Portal

export const ModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-ia-overlay backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
ModalOverlay.displayName = 'ModalOverlay'

export interface ModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showClose?: boolean
}

const SIZE_WIDTHS = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[960px]',
}

export const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(({ className, size = 'md', showClose = true, children, ...props }, ref) => (
  <ModalPortal>
    <ModalOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 w-[92vw] -translate-x-1/2 -translate-y-1/2',
        SIZE_WIDTHS[size],
        'rounded-lg border border-ia-border bg-card shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'focus:outline-none',
        className,
      )}
      {...props}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close
          aria-label="Close"
          className="absolute right-3 top-3 rounded-pill h-8 w-8 flex items-center justify-center text-ia-text-tertiary transition-all hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-ia-sidebar-active-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X size={16} strokeWidth={1.75} />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </ModalPortal>
))
ModalContent.displayName = 'ModalContent'

export const ModalHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn('px-6 pt-6 pb-3 flex flex-col gap-1', className)} {...props} />

export const ModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-heading-md text-ia-text', className)}
    {...props}
  />
))
ModalTitle.displayName = 'ModalTitle'

export const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-body-sm text-ia-text-secondary', className)}
    {...props}
  />
))
ModalDescription.displayName = 'ModalDescription'

export const ModalBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn('px-6 py-3', className)} {...props} />

export const ModalFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      'px-6 py-4 border-t border-ia-border flex items-center justify-end gap-2',
      className,
    )}
    {...props}
  />
)

/**
 * ConfirmDialog — thin wrapper around Modal for simple yes/no flows.
 */
export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  icon?: LucideIcon
  /** When provided, user must type this string to enable the confirm button (destructive safeguard). */
  typeToConfirm?: string
  onConfirm: () => void | Promise<void>
  loading?: boolean
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  icon,
  typeToConfirm,
  onConfirm,
  loading,
}) => {
  const [typed, setTyped] = React.useState('')
  const canConfirm = !typeToConfirm || typed === typeToConfirm
  React.useEffect(() => {
    if (!open) setTyped('')
  }, [open])
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm">
        <ModalHeader>
          {icon && (
            <div className="mb-3">
              <IconCircle icon={icon} tone={destructive ? 'error' : 'info'} size="lg" />
            </div>
          )}
          <ModalTitle>{title}</ModalTitle>
          {description && <ModalDescription>{description}</ModalDescription>}
        </ModalHeader>
        {typeToConfirm && (
          <ModalBody>
            <label className="text-body-sm text-ia-text-secondary">
              Type{' '}
              <code className="px-1 py-[2px] rounded-sm bg-ia-code font-mono text-code">
                {typeToConfirm}
              </code>{' '}
              to confirm:
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-2 w-full h-10 rounded-md border border-ia-border bg-ia-input-bg px-3 text-body-md text-ia-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              autoComplete="off"
            />
          </ModalBody>
        )}
        <ModalFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            disabled={!canConfirm}
            loading={loading}
            onClick={async () => {
              await onConfirm()
              onOpenChange(false)
            }}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
